/**
 * Web mock for `lottie-react-native`.
 *
 * `lottie-react-native@6.x` resolves its `index.web.tsx` entry on web, which
 * imports the optional peer dependency `@lottiefiles/react-lottie-player`. That
 * peer is not installed in this project, so the real web entry throws at module
 * load time ("the module @lottiefiles/react-lottie-player is missing") and the
 * whole web bundle fails to mount.
 *
 * This mock provides an API-compatible, dependency-free stand-in that is aliased
 * for the web build in `webpack.config.js`. It renders a lightweight branded
 * placeholder in place of the animation and — crucially — still fires
 * `onAnimationFinish`, because several screens (SplashScreen, success/failure
 * payment & entry screens) drive their navigation off that callback. Without it
 * the web app would stall on the splash/result screens.
 *
 * Native builds are unaffected: this alias only applies to the webpack (web)
 * bundle; iOS/Android keep the real `lottie-react-native` implementation.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { View } from 'react-native';

// Default visual duration (ms) used to emulate a single, non-looping animation
// play-through before `onAnimationFinish` is invoked on web.
const DEFAULT_FINISH_DELAY = 1500;

/**
 * Drop-in replacement for the native `LottieView`.
 *
 * Supported props mirror the subset used across the app: `source`, `autoPlay`,
 * `loop`, `speed`, `duration`, `resizeMode`, `renderMode`, `style` and
 * `onAnimationFinish`. The imperative ref exposes no-op `play`, `reset`,
 * `pause` and `resume` methods so existing `ref.current?.play()` calls are safe.
 *
 * @param {object} props
 * @param {boolean} [props.autoPlay] Whether playback starts automatically.
 * @param {boolean} [props.loop] When true, `onAnimationFinish` is not fired.
 * @param {number} [props.duration] Optional animation duration in ms.
 * @param {Function} [props.onAnimationFinish] Called once when a non-looping
 *   animation would have completed.
 * @param {object|Array} [props.style] React Native style for the container.
 * @param {React.Ref} ref Forwarded ref exposing imperative playback no-ops.
 * @returns {JSX.Element}
 */
const LottieView = forwardRef(function LottieViewWebMock(props, ref) {
  const {
    autoPlay = false,
    loop = false,
    duration,
    speed,
    onAnimationFinish,
    style,
    // The remaining lottie-only props (source, resizeMode, renderMode, …) are
    // intentionally ignored on web.
    ...rest
  } = props;

  const finishedRef = useRef(false);

  useImperativeHandle(
    ref,
    () => ({
      play: () => {},
      reset: () => {
        finishedRef.current = false;
      },
      pause: () => {},
      resume: () => {},
    }),
    [],
  );

  useEffect(() => {
    if (loop || typeof onAnimationFinish !== 'function') {
      return undefined;
    }
    if (!autoPlay) {
      return undefined;
    }

    const baseDelay = typeof duration === 'number' && duration > 0
      ? duration
      : DEFAULT_FINISH_DELAY;
    const delay = speed && speed > 0 ? baseDelay / speed : baseDelay;

    const timer = globalThis.setTimeout(() => {
      if (finishedRef.current) {
        return;
      }
      finishedRef.current = true;
      onAnimationFinish(false);
    }, delay);

    return () => globalThis.clearTimeout(timer);
  }, [autoPlay, loop, duration, speed, onAnimationFinish]);

  // Render an empty, correctly-sized container so layout is preserved without
  // pulling in any animation runtime.
  return <View style={style} pointerEvents="none" {...rest} />;
});

export default LottieView;
